import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Filter, X } from 'lucide-react';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';

import LocaleFilter from './LocaleFilter';
import ChannelFilter from './ChannelFilter';
import FamilyFilter from './FamilyFilter';

export interface ReportFiltersState {
  locale?: string;
  channel?: string;
  brand?: string;
  family?: string;
}

interface ReportFiltersProps {
  filters: ReportFiltersState;
  onFiltersChange: (filters: ReportFiltersState) => void;
  availableFilters?: ('locale' | 'channel' | 'family')[];
}

const ReportFilters: React.FC<ReportFiltersProps> = ({
  filters,
  onFiltersChange,
  availableFilters = ['locale', 'channel'],
}) => {
  // Get translations
  const i18n = useReportspageTranslations();

  const handleLocaleChange = (locale: string) => {
    onFiltersChange({
      ...filters,
      locale: locale === 'all' ? undefined : locale,
    });
  };

  const handleChannelChange = (channel: string) => {
    onFiltersChange({
      ...filters,
      channel: channel === 'all' ? undefined : channel,
    });
  };

  const handleFamilyChange = (family: string) => {
    onFiltersChange({
      ...filters,
      family: family === 'all' ? undefined : family,
    });
  };

  const handleResetFilters = () => {
    onFiltersChange({});
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== undefined);

  // Create an ordered array of filter elements based on the requested order
  const renderFilters = () => {
    const filterComponents = [];

    if (availableFilters.includes('family')) {
      filterComponents.push(
        <FamilyFilter key="family" value={filters.family} onChange={handleFamilyChange} />
      );
    }

    if (availableFilters.includes('locale')) {
      filterComponents.push(
        <LocaleFilter key="locale" value={filters.locale} onChange={handleLocaleChange} />
      );
    }

    if (availableFilters.includes('channel')) {
      filterComponents.push(
        <ChannelFilter key="channel" value={filters.channel} onChange={handleChannelChange} />
      );
    }

    return filterComponents;
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center text-lg font-medium">
              <Filter className="mr-2 size-5" />
              {i18n.filters.title}
            </h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleResetFilters} className="h-8">
                <X className="mr-2 size-4" />
                {i18n.buttons.filters.reset}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-4">{renderFilters()}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportFilters;
